import React from "react";
import { useLocation } from "react-router-dom";
import { useModuleConfig } from "../hooks/useModuleConfig";
import { ModuleConfigProvider } from "../contexts/ModuleConfigContext";
import { SharedDashboardProvider } from "@variphigen/shared-dashboards";
import { loadSharedDashboardConfig } from "../runtime/loadSharedDashboardConfig";
import { AuthTransitionScreen } from "../Components/AuthTransitionScreen";
import { useAuthSession, getAuthCallbackPath } from "../auth/session";
import { isAppSessionActive } from "../auth/session/authGate";
import App from "../App";

interface ModuleConfigBootstrapProps {
  applicationId: string;
}

function useIsAuthCallbackRoute(): boolean {
  const { pathname } = useLocation();
  const callbackPath = getAuthCallbackPath();
  return pathname === callbackPath || pathname.endsWith(callbackPath);
}

export const ModuleConfigBootstrap: React.FC<ModuleConfigBootstrapProps> = ({
  applicationId,
}) => {
  const onAuthCallback = useIsAuthCallbackRoute();
  const { data: sessionUser } = useAuthSession();
  const authReady = isAppSessionActive() && Boolean(sessionUser);
  const shouldFetchModuleConfig =
    Boolean(applicationId) && authReady && !onAuthCallback;

  const { data, isLoading, isError } = useModuleConfig(applicationId, {
    enabled: shouldFetchModuleConfig,
  });
  const baseConfig = loadSharedDashboardConfig();

  const waitingForModuleConfig =
    shouldFetchModuleConfig && isLoading && !isError && !data;

  if (waitingForModuleConfig) {
    return <AuthTransitionScreen />;
  }

  const modules = data?.modules;

  return (
    <ModuleConfigProvider
      modules={modules}
      version={data?.version ?? 0}
      isLoading={shouldFetchModuleConfig && isLoading}
    >
      <SharedDashboardProvider config={baseConfig}>
        <App />
      </SharedDashboardProvider>
    </ModuleConfigProvider>
  );
};

export default ModuleConfigBootstrap;
